/**
 * Android Intent URL builder for ESC/POS direct printing from a browser.
 *
 * Constructs an Android intent:// URL that triggers a printing app
 * (e.g. "RawBT", "PrinterShare", or a custom ESC/POS bridge app)
 * installed on the Android device.
 *
 * Format:
 *   intent://HOST:PORT#Intent;scheme=tcp;package=...;S.data=<base64>;end
 */

const DEFAULT_PRINT_PACKAGE = 'a.android.print'; // RawBT bridge package

export function buildPrintIntent(
  printerIp: string,
  printerPort: string | number = 9100,
  data: string,
  packageName: string = DEFAULT_PRINT_PACKAGE,
): string {
  // Encode data as base64 if it isn't already
  const encoded = isBase64(data) ? data : btoa(unescape(encodeURIComponent(data)));

  return [
    `intent://${printerIp}:${printerPort}`,
    `#Intent`,
    `scheme=tcp`,
    `package=${packageName}`,
    `S.data=${encodeURIComponent(encoded)}`,
    `end`,
  ].join(';');
}

function isBase64(str: string): boolean {
  try {
    return btoa(atob(str)) === str;
  } catch {
    return false;
  }
}

/**
 * Build a minimal ESC/POS text payload for a receipt.
 * Returns a string that can be sent to buildPrintIntent.
 */
export function buildEscPosText(lines: string[], width: number = 42): string {
  const ESC = '\x1B';
  const INIT = `${ESC}@`; // Initialize printer
  const CUT = `${ESC}d\x04`; // Feed 4 lines and cut

  const body = lines
    .map((line) => {
      // Truncate/pad to printer width
      if (line.length > width) return line.slice(0, width);
      return line;
    })
    .join('\n');

  return `${INIT}${body}\n${CUT}`;
}

/**
 * Format a receipt line item as ESC/POS aligned text.
 * Produces:  "Name                   $12.00"
 */
export function formatReceiptLine(
  label: string,
  value: string,
  width: number = 42,
): string {
  const spaces = width - label.length - value.length;
  if (spaces <= 0) {
    return `${label.slice(0, width - value.length - 1)} ${value}`;
  }
  return `${label}${' '.repeat(spaces)}${value}`;
}
